import {
  BrowseRequestDto,
  CategoryDto,
  DeliverySlotDto,
  EssentialProductFilterDto,
  PageMetadataDto,
  PageRequestDto,
  ProductDto,
  ProductFacetsDto,
  ProductGroupingDto,
  ProductSearchFilterDto,
  ProductSearchRequestDto,
  ProductSearchResultDto,
  ProductUnitDto,
  ShoppingCartItemDto,
  ShoppingListDto,
} from '@/api';
import * as _ from 'lodash';

export interface ShoppingListItemDto {
  list: ShoppingListDto | null;
  item: ShoppingCartItemDto;
}

export interface ProductTuple {
  unit: ProductUnitDto;
  product: ProductDto;
}

export interface CategoryMap {
  category: CategoryDto;
  children: CategoryDto[];
}

export interface ProductSearchResult {
  filter?: ProductSearchFilterDto;
  queryDurationMs?: number;
  products: ProductTuple[];
  page?: PageMetadataDto;
  facets: ProductFacetsDto;
}

export interface ProductGrouping {
  category: CategoryDto;
  memberCount: number;
  products: ProductTuple[];
}

export interface GroupedProducts {
  filter?: ProductSearchFilterDto;
  queryDurationMs?: number;
  groupings: ProductGrouping[];
  page: PageMetadataDto;
  facets: ProductFacetsDto;
}

export interface GroupedDeliverySlot {
  day: string;
  slots: DeliverySlotDto[];
}

export class SearchUtility {
  public static readonly EmptyPageResult: PageMetadataDto = {
    totalElements: 0,
    totalPages: 0,
    number: 0,
    size: 20,
  };

  public static readonly EmptyBrowseResult: GroupedProducts = {
    groupings: [],
    page: SearchUtility.EmptyPageResult,
    facets: {
      attributes: [],
      properties: [],
      parentCategories: [],
      parentCategoryFacets: [],
      childCategoryFacets: [],
      availablePriceRange: {},
    },
  };

  public static readonly EmptySearchResult: ProductSearchResult = {
    page: SearchUtility.EmptyPageResult,
    products: [],
    facets: {
      attributes: [],
      properties: [],
      parentCategories: [],
      parentCategoryFacets: [],
      childCategoryFacets: [],
      availablePriceRange: {},
    },
  };
  public static readonly EmptySearchResultDto: ProductSearchResultDto = {
    page: SearchUtility.EmptyPageResult,
    products: [],
    facets: {
      attributes: [],
      properties: [],
      parentCategories: [],
      parentCategoryFacets: [],
      childCategoryFacets: [],
      availablePriceRange: {},
    },
  };

  public static EmptyPageRequest: PageRequestDto = {
    page: 0,
    size: 20,
  };

  public static EmptySearchRequest: ProductSearchRequestDto = {
    queryString: '',
    page: SearchUtility.EmptyPageRequest,
    filter: {
      attributes: [],
      properties: [],
    },
  };

  public static EmptyBrowseRequest: BrowseRequestDto = {
    filter: {
      attributes: [],
      properties: [],
      categoryId: '',
    },
    page: SearchUtility.EmptyPageRequest,
  };

  public static splitByUnit(products: ProductDto[], filter?: ProductSearchFilterDto): ProductTuple[] {
    const limitToEssentials = filter?.essential && filter?.essential !== EssentialProductFilterDto.IGNORE;
    const productTuples: ProductTuple[] = [];
    products.forEach((product) => {
      const essentialUnits = product.essentialUnits || [];
      product.units.units.forEach((unit) => {
        if (unit.isVisible && (!limitToEssentials || essentialUnits.includes(unit.abbreviation))) {
          productTuples.push({unit, product});
        }
      });
    });
    return productTuples;
  }

  public static splitGroupingsByUnit(
    groupings: ProductGroupingDto[], filter?: ProductSearchFilterDto,
  ): ProductGrouping[] {
    const productGroupings: ProductGrouping[] = [];
    groupings.forEach((grouping) => {
      const groupedTuples = SearchUtility.splitGroupingByUnit(grouping, filter);
      if (groupedTuples !== null) {
        productGroupings.push(groupedTuples);
      }
    });
    return productGroupings;
  }

  public static splitGroupingByUnit(
    grouping: ProductGroupingDto, filter?: ProductSearchFilterDto,
  ): ProductGrouping | null {
    const productTuples = SearchUtility.splitByUnit(grouping.products, filter);
    if (productTuples.length > 0) {
      return {
        category: grouping.category,
        memberCount: productTuples.length,
        products: productTuples,
      };
    } else {
      return null;
    }
  }

  public static mergeGroupings(
    existing: ProductGrouping[], next: ProductGroupingDto[], filter?: ProductSearchFilterDto,
  ): ProductGrouping[] {
    const result = existing.map((original) => _.cloneDeep(original));
    const existingIndices: { [k: string]: number } = {};
    result.forEach((grouping, index) => existingIndices[grouping.category.id] = index);
    next.forEach((nextGrouping) => {
      const convertedGrouping = SearchUtility.splitGroupingByUnit(nextGrouping, filter);
      if (convertedGrouping && existingIndices.hasOwnProperty(nextGrouping.category.id)) {
        const existingGrouping = result[existingIndices[nextGrouping.category.id]];
        existingGrouping.products = existingGrouping.products.concat(convertedGrouping.products);
        existingGrouping.memberCount += nextGrouping.memberCount;
      } else if (convertedGrouping) {
        result.push(convertedGrouping);
      }
    });
    return result;
  }

  public static countProductsIn(groupings: ProductGrouping[]): number {
    return groupings.map((grouping) => grouping.memberCount)
      .reduce((sum, next) => sum + next, 0);
  }

  public static countDistinctProductsIn(groupings: ProductGrouping[]): number {
    const distinctProducts: ProductDto[] = [];
    groupings.map((grouping) => grouping.products).forEach(
      (productTuples) => {
        productTuples.forEach(
          (productTuple) => {
            if (distinctProducts.filter((product) => product.sku === productTuple.product.sku).length === 0) {
              distinctProducts.push(productTuple.product);
            }
          },
        );
      },
    );
    return distinctProducts.length;
  }
}
